// AdminQuickPulse — at-a-glance feed of latest pending requests on the dashboard.
import { useEffect, useState } from "react";
import api from "@/lib/api";

const STATUS_PILL = {
  new:        { bg: "var(--cinnabar)",   fg: "#fff",        label: "Nouveau" },
  contacted:  { bg: "var(--gold)",       fg: "var(--ink)",  label: "Contacté" },
  confirmed:  { bg: "var(--moss)",       fg: "#fff",        label: "Confirmé" },
  declined:   { bg: "#7a6f60",           fg: "#fff",        label: "Refusé" },
  cancelled:  { bg: "#7a6f60",           fg: "#fff",        label: "Annulé" },
  pending:    { bg: "var(--cinnabar)",   fg: "#fff",        label: "À traiter" },
};

const STATUS_OPTS = [
  { value: "new",       label: "Nouveau" },
  { value: "contacted", label: "Contacté" },
  { value: "confirmed", label: "Confirmé" },
  { value: "declined",  label: "Refusé" },
  { value: "cancelled", label: "Annulé" },
];

function Pill({ status }) {
  const s = STATUS_PILL[status] || STATUS_PILL.new;
  return (
    <span className="font-caps text-[0.55rem]"
          style={{ background: s.bg, color: s.fg, padding: "2px 8px", letterSpacing: "0.14em" }}>
      {s.label}
    </span>
  );
}

function Row({ item, onClick }) {
  const name = `${item.first_name || ""} ${item.last_name || ""}`.trim() || item.name || "—";
  return (
    <button onClick={onClick} data-testid={`qp-row-${item.id}`}
            className="w-full text-left flex items-start gap-3 py-2.5 transition-colors"
            style={{ borderBottom: "1px solid var(--border)", background: "transparent", border: "none", cursor: "pointer" }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "var(--parchment-2)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
      <div className="font-mono text-[0.7rem] min-w-[68px]" style={{ color: "var(--muted)" }}>
        {(item.created_at || "").slice(5, 10)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-[0.95rem] truncate">{name}</div>
        <div className="font-body text-xs truncate" style={{ color: "var(--muted)" }}>{item._info}</div>
      </div>
      <Pill status={item.status || "new"} />
    </button>
  );
}

function Panel({ title, items, kind, emptyLabel, ctaLabel, onCta, accent, badge, onOpen }) {
  return (
    <div className="admin-card" data-testid={`quick-pulse-${kind}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span style={{ color: accent, fontSize: "1.2rem" }}>◇</span>
          <div className="font-display text-lg">{title}</div>
          {badge > 0 && (
            <span className="font-caps text-[0.55rem]"
                  style={{ background: "var(--cinnabar)", color: "#fff", padding: "2px 7px", borderRadius: 10, letterSpacing: "0.1em" }}>
              {badge} non lue{badge > 1 ? "s" : ""}
            </span>
          )}
        </div>
        <button onClick={onCta} className="font-caps text-[0.6rem]"
                style={{ color: "var(--cinnabar)", letterSpacing: "0.18em", background: "none", border: "none", cursor: "pointer" }}>
          {ctaLabel} →
        </button>
      </div>
      {items.length === 0 ? (
        <div className="font-body text-sm italic py-4 text-center" style={{ color: "var(--muted)" }}>
          {emptyLabel}
        </div>
      ) : (
        <div>
          {items.slice(0, 3).map((it) => <Row key={it.id} item={it} onClick={() => onOpen(kind, it)} />)}
        </div>
      )}
    </div>
  );
}

function RequestModal({ kind, item, onClose, onChanged }) {
  const [status, setStatus] = useState(item.status || "new");
  const [adminNotes, setAdminNotes] = useState(item.admin_notes || "");
  const [busy, setBusy] = useState(false);

  const isBerg = kind === "bergerie";
  const isSej = kind === "sejour";
  const fullName = `${item.first_name || ""} ${item.last_name || ""}`.trim() || item.name || "—";

  const save = async () => {
    if (kind === "prereg") { onClose(); return; }  // pre-registrations handled in their own tab
    setBusy(true);
    try {
      const url = `/admin/${kind}/requests/${item.id}`;
      await api.patch(url, { status, admin_notes: adminNotes });
      onChanged();
      onClose();
    } finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} data-testid="qp-modal-backdrop"
         style={{
           position: "fixed", inset: 0, background: "rgba(20,16,12,0.55)",
           zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center",
           padding: "1rem",
         }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="qp-modal"
           style={{ background: "var(--parchment)", maxWidth: 560, width: "100%",
                    boxShadow: "0 30px 80px rgba(0,0,0,0.4)", padding: "1.75rem 2rem",
                    maxHeight: "90vh", overflow: "auto" }}>
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="font-caps text-[0.6rem] mb-1" style={{ color: "var(--gold)", letterSpacing: "0.24em" }}>
              {isBerg ? "◇ BERGERIE PÈLERINE ◇" : isSej ? "◇ SÉJOUR AU PRIEURÉ ◇" : "◇ PRÉ-INSCRIPTION FÊTES ◇"}
            </div>
            <h3 className="font-display text-2xl">{fullName}</h3>
            <div className="font-body text-sm" style={{ color: "var(--muted)" }}>
              {item.email}{item.phone ? ` · ${item.phone}` : ""}
            </div>
          </div>
          <button onClick={onClose} data-testid="qp-modal-close"
                  style={{ background: "none", border: "none", fontSize: "1.4rem", cursor: "pointer", color: "var(--muted)" }}>
            ×
          </button>
        </div>

        <div className="space-y-3 font-body text-sm" style={{ color: "var(--ink-2)" }}>
          {item.group_name && <Field label="Groupe">{item.group_name}</Field>}
          {isBerg && <>
            <Field label="Personnes">{item.nb_people}</Field>
            <Field label="Nuits">{item.nb_nights}</Field>
            <Field label="Date d'arrivée">{item.arrival_date || "—"}</Field>
            {item.notes && <Field label="Notes">{item.notes}</Field>}
          </>}
          {isSej && <>
            <Field label="Type">{item.sejour_type}</Field>
            <Field label="Personnes">{item.nb_people}</Field>
            <Field label="Arrivée → Départ">{item.arrival_date || "—"} → {item.departure_date || "—"}</Field>
            {item.project && <Field label="Projet">{item.project}</Field>}
          </>}
          {kind === "prereg" && <>
            <Field label="Personnes">{item.nb_people}</Field>
            <Field label="Provenance">{item.country || "—"}{item.city ? ` · ${item.city}` : ""}</Field>
            {item.organization && <Field label="Organisation">{item.organization}</Field>}
          </>}
        </div>

        {kind !== "prereg" && (
          <div className="mt-6 space-y-3">
            <label className="block">
              <span className="font-caps text-[0.6rem]" style={{ color: "var(--ink-2)", letterSpacing: "0.2em" }}>Statut</span>
              <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="qp-modal-status"
                      className="form-input mt-1.5">
                {STATUS_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="font-caps text-[0.6rem]" style={{ color: "var(--ink-2)", letterSpacing: "0.2em" }}>Notes admin</span>
              <textarea value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} data-testid="qp-modal-notes"
                        rows="3" className="form-input mt-1.5" />
            </label>
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="btn btn-outline" style={{ fontSize: "0.72rem", padding: "0.6rem 1.1rem" }}>
            Annuler
          </button>
          {kind !== "prereg" && (
            <button onClick={save} disabled={busy} data-testid="qp-modal-save"
                    className="btn btn-primary" style={{ fontSize: "0.72rem", padding: "0.6rem 1.4rem" }}>
              {busy ? "Enregistrement…" : "Enregistrer"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex gap-3">
      <span className="font-caps text-[0.55rem] min-w-[110px]" style={{ color: "var(--muted)", letterSpacing: "0.16em" }}>
        {label}
      </span>
      <span className="flex-1">{children}</span>
    </div>
  );
}

export default function AdminQuickPulse({ onNavigate }) {
  const [data, setData] = useState({ bergerie: [], sejour: [], prereg: [], counts: { bergerie: 0, sejour: 0, prereg: 0 } });
  const [modalState, setModalState] = useState(null); // {kind, item}

  const load = async () => {
    try {
      const [bResp, sResp, pResp] = await Promise.all([
        api.get("/admin/bergerie/requests").catch(() => ({ data: { items: [] } })),
        api.get("/admin/sejour/requests").catch(() => ({ data: { items: [] } })),
        api.get("/admin/presence/pre-registrations").catch(() => ({ data: { items: [] } })),
      ]);

      const enrich = (items, infoFn) => items.map((it) => ({ ...it, _info: infoFn(it) }));
      const bItems = enrich(bResp.data.items || [], (it) =>
        `${it.nb_people} pers. · ${it.nb_nights} nuit(s)${it.group_name ? ` · ${it.group_name}` : ""}`);
      const sItems = enrich(sResp.data.items || [], (it) =>
        `${it.sejour_type || ""} · ${it.nb_people} pers.${it.group_name ? ` · ${it.group_name}` : ""}`);
      const pItems = enrich(pResp.data.items || [], (it) =>
        `${it.nb_people || 0} pers. · ${it.country || ""}`);
      setData({
        bergerie: bItems, sejour: sItems, prereg: pItems,
        counts: {
          bergerie: bItems.filter((x) => (x.status || "new") === "new").length,
          sejour:   sItems.filter((x) => (x.status || "new") === "new").length,
          prereg:   pItems.filter((x) => (x.status || "new") === "new" || x.status === "pending").length,
        },
      });
    } catch {/* ignore */}
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4" data-testid="admin-quick-pulse">
      <div className="font-caps text-[0.65rem]"
           style={{ color: "var(--gold)", letterSpacing: "0.26em" }}>
        ◇ ACTIVITÉ RÉCENTE ◇
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel kind="bergerie" title="Bergerie pèlerine" accent="var(--gold)"
               items={data.bergerie} emptyLabel="Aucune demande pour le moment."
               ctaLabel="Voir tout" badge={data.counts.bergerie}
               onCta={() => onNavigate?.("hospitalite")}
               onOpen={(k, item) => setModalState({ kind: k, item })} />
        <Panel kind="sejour" title="Séjour au Prieuré" accent="var(--cinnabar)"
               items={data.sejour} emptyLabel="Aucune demande pour le moment."
               ctaLabel="Voir tout" badge={data.counts.sejour}
               onCta={() => onNavigate?.("hospitalite")}
               onOpen={(k, item) => setModalState({ kind: k, item })} />
        <Panel kind="prereg" title="Pré-inscriptions Fêtes" accent="var(--moss)"
               items={data.prereg} emptyLabel="Aucune pré-inscription."
               ctaLabel="Voir tout" badge={data.counts.prereg}
               onCta={() => onNavigate?.("presence")}
               onOpen={(k, item) => setModalState({ kind: k, item })} />
      </div>
      {modalState && (
        <RequestModal kind={modalState.kind} item={modalState.item}
                       onClose={() => setModalState(null)}
                       onChanged={load} />
      )}
    </div>
  );
}
